import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import IndividualisedRehabPlanForm from "./IndividualisedRehabPlanForm";
import {
    fetchRehabPlan,
    updateRehabPlan,
    clearError,
} from "../../../store/slices/individualisedRehabPlanSlice";

const EditIndividualisedRehabPlan = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentRehabPlan, isLoading, error } = useSelector(
        (state) => state.individualisedRehabPlan
    );

    const [formData, setFormData] = useState(null);

    // --------------------------------------------------------------
    // 1. Fetch record + transform to form shape
    // --------------------------------------------------------------
    useEffect(() => {
        dispatch(fetchRehabPlan(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentRehabPlan) {
            const transformed = {
                patientId: patientId || currentRehabPlan.patientId,
                caseId: caseId || currentRehabPlan.caseId,
                domain: currentRehabPlan.domain || "",
                strengths: currentRehabPlan.strengths?.map((s) => ({
                    value: s.value || "",
                })) || [{ value: "" }],

                weaknesses: currentRehabPlan.weaknesses?.map((w) => ({
                    value: w.value || "",
                })) || [{ value: "" }],

                objectives:
                    currentRehabPlan.objectives?.map((obj) => ({
                        name: obj.name || "",
                        obj_status: obj.obj_status || "Pending",
                        obj_date: obj.obj_date ? obj.obj_date.split("T")[0] : "",
                        actions:
                            obj.actions?.map((a) => ({
                                action_name: a.action_name || "",
                                location: a.location || "",
                                duration: a.duration || "",
                                frequency: a.frequency || "",
                            })) || [],
                    })) || [],
            };
            setFormData(transformed);
        }
    }, [currentRehabPlan]);

    // --------------------------------------------------------------
    // 2. Validation (same as Add)
    // --------------------------------------------------------------
    const validate = () => {
        const errs = {};

        if (!formData?.domain) errs.domain = "Please select a domain.";
        if (!formData?.strengths?.some((s) => s.value.trim()))
            errs.strengths = "Add at least one strength.";
        if (!formData?.weaknesses?.some((w) => w.value.trim()))
            errs.weaknesses = "Add at least one weakness.";

        formData?.objectives?.forEach((obj, i) => {
            if (!obj.name.trim()) errs[`obj_name_${i}`] = "Objective name required.";
            if (!obj.obj_status) errs[`obj_status_${i}`] = "Select a status.";
            if (!obj.obj_date) errs[`obj_date_${i}`] = "Date is required.";

            obj.actions.forEach((act, j) => {
                if (!act.action_name.trim())
                    errs[`act_name_${i}_${j}`] = "Action name required.";
            });
        });

        return errs;
    };

    // --------------------------------------------------------------
    // 3. Submit → updateRehabPlan
    // --------------------------------------------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length) {
            Swal.fire({
                title: "Missing fields",
                html: Object.values(validationErrors).join("<br/>"),
                icon: "warning",
            });
            return;
        }

        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            domain: formData.domain,
            strengths: formData.strengths.map((s) => ({ value: s.value.trim() })),
            weaknesses: formData.weaknesses.map((w) => ({ value: w.value.trim() })),
            objectives: formData.objectives.map((obj) => ({
                name: obj.name.trim(),
                obj_status: obj.obj_status,
                obj_date: obj.obj_date,
                actions: obj.actions.map((a) => ({
                    action_name: a.action_name.trim(),
                    location: a.location?.trim() || null,
                    duration: a.duration?.trim() || null,
                    frequency: a.frequency?.trim() || null,
                })),
            })),
        };

        const result = await dispatch(updateRehabPlan({ case_id: caseId, rehabPlanData: payload }));

        if (updateRehabPlan.fulfilled.match(result)) {
            Swal.fire({
                title: "Updated!",
                text: "Rehab plan updated successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } else {
            Swal.fire({
                title: "Error",
                text: result.payload || "Failed to update plan.",
                icon: "error",
            });
        }
    };

    // --------------------------------------------------------------
    // 4. Render
    // --------------------------------------------------------------
    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/individualised-rehab-plan/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        {/* Redux error */}
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show">
                                <strong>Error:</strong> {error}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => dispatch(clearError())}
                                />
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <IndividualisedRehabPlanForm
                                formData={formData}
                                setFormData={setFormData}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditIndividualisedRehabPlan;